import type { AnchorHTMLAttributes, FunctionComponent } from 'react';
import styles from './CTAButton.module.scss';
import LinkWrapper from '../LinkWrapper/linkWrapper';

/**
 * CTA Buttons are just links, yo
 * @category CTAButton
 * @extends AnchorHTMLAttributes<HTMLAnchorElement>
 * */

// TODO: update variants to support sign-up-flow-iframe when we are making site wide update

export type CTAColorVariant =
  | 'primary-blue'
  | 'primary-yellow'
  | 'spark-yellow'
  | 'primary-black'
  | 'secondary'
  | 'text-link'
  | 'aa-primary-blue'
  | 'aa-secondary'
  | 'aa-text-link';
export type CTAElementType =
  | 'LINK'
  | 'SIGN_UP_CTA_PRIMARY'
  | 'SIGN_UP_CTA_SECONDARY'
  | 'SIGN_IN_CTA';

const wallyWeekSignup = {
  production:
    'https://www.walmart.com/plus/plans?code=eventoffers&programName=discountedmembership&programId=discmemb23',
  nonprod:
    'https://www-teflon.walmart.com/plus/plans?code=eventoffers&programName=discountedMembership&programId=discmemb23',
};

const regularSignup = {
  production: 'https://www.walmart.com/plus/plans',
  nonprod: 'https://www-teflon.walmart.com/plus/plans',
};

const urlSet = process.env.week === 'Walmart' ? wallyWeekSignup : regularSignup;
const baseSignupURL =
  process.env.environment === 'production' ? urlSet.production : urlSet.nonprod;

interface CTAButtonProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  variant: CTAColorVariant;
  size?: 'default' | 'thin' | 'aa';
  elementPosition: string;
  elementName?: string;
  elementType?: CTAElementType;
  excludeFromTracking?: boolean;
  tabIndex?: number;
}

const CTAButton: FunctionComponent<CTAButtonProps> = ({
  variant = 'primary-blue',
  size = 'default',
  href,
  children,
  className,
  elementPosition,
  elementName,
  elementType,
  excludeFromTracking = false,
  tabIndex = 0,
  ...props
}: CTAButtonProps) => {
  // checks to ensure button variant we get is a valid type, defaults to primary-blue if it is not valid
  const possibleButtonVariants = [
    'primary-blue',
    'primary-black',
    'primary-yellow',
    'spark-yellow',
    'secondary',
    'text-link',
    'aa-primary-blue',
    'aa-secondary',
    'aa-text-link',
  ];
  const isValid = possibleButtonVariants.some((button) =>
    variant.includes(button)
  );

  if (!isValid) {
    variant = 'primary-blue';
  }

  const segmentTracking = {
    elementName: elementName || 'CTA',
    elementPosition,
    elementType,
  };

  return (
    <LinkWrapper
      data-test='cta-link'
      segmentTracking={segmentTracking}
      href={href || baseSignupURL}
      className={[
        styles[`cta-${variant}`],
        styles[
          `${variant.includes('text-link') ? 'text-link' : 'cta'}-size-${size}`
        ],
        className,
      ].join(' ')}
      {...props}
      disableUrlParamPersist={excludeFromTracking}
      newRelicActionName='ctaSignupButton'
      onClick={(e) => {
        if (props.onClick) {
          props.onClick(e);
        }
      }}
      tabIndex={tabIndex}
    >
      {children || 'Claim your free trial'}
    </LinkWrapper>
  );
};

export default CTAButton;
